import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Params } from '@angular/router'
import { combineLatest, forkJoin, map, Observable, of, switchMap } from 'rxjs'

import {
  CollectionRecord,
  Enums,
  HuntPackageRecord,
  ThreatProfile,
} from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'

import { CollectionData, CollectionService } from './collection.service'
import { SearchStateExtended, TagDictionary } from './search-state.service'

export type SearchResultItem = (
  | HuntPackageRecord
  | ThreatProfile
  | CollectionRecord
) &
  SearchResultAdditionalMetadata

// Response from API
export interface SearchResultData {
  total: number
  results: Array<SearchResultItem>

  count: {
    cyborg_threat_profile: number
    cyborg_hunt_packages: number
    cyborg_collections: number
  }
}

export interface SearchResultAdditionalMetadata {
  index: Enums.CyborgIndexEnum
}

export type ResolvedSearchResultItem = (
  | HuntPackageRecord
  | ThreatProfile
  | CollectionData
) &
  SearchResultAdditionalMetadata

// Post-processed search response, with any links hydrated to full objects
export interface ResolvedSearchResultData extends Omit<
  SearchResultData,
  'results'
> {
  results: Array<ResolvedSearchResultItem>
}

@Injectable({ providedIn: 'root' })
export class SearchDataService {
  private http = inject(HttpClient)
  private collectionService = inject(CollectionService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  /**
   * Query the search with the passed search state, returning fully resolved search results with respective input context and tag counts.
   * @param searchState the state to search for store
   */
  public fetchResults(searchState: SearchStateExtended): Observable<{
    state: SearchStateExtended
    data: ResolvedSearchResultData
    tags: TagDictionary
  }> {
    const searchParams = this.stateToSearchParams(searchState)
    const tagParams = this.stateToTagParams(searchState)

    const resultSearch$: Observable<ResolvedSearchResultData> =
      this.getSearchResults(searchParams)
    const tagSearch$: Observable<TagDictionary> = this.getTagResults(tagParams)

    return forkJoin({
      state: of(searchState),
      data: resultSearch$,
      tags: tagSearch$,
    })
  }

  public stateToTagParams(searchState: SearchStateExtended): Params {
    const indexes: Enums.CyborgIndexEnum[] = searchState.library
    const days = searchState.lastUpdated
    const { filters, term } = searchState

    const tagParams: Params = {
      days: days,
      indexes,
    }

    for (const key in filters) {
      if (filters[key].length) {
        const items: string[] = filters[key].map(
          (name) => `${encodeURIComponent(name)}`,
        )
        tagParams[key] = items
      }
    }
    tagParams['term'] = term || ['*']
    if (searchState.community) {
      tagParams['community'] = 'true'
    }
    return tagParams
  }

  public stateToSearchParams(searchState: SearchStateExtended): Params {
    const indexes: Enums.CyborgIndexEnum[] = searchState.library
    const days = searchState.lastUpdated
    const { filters, page, size, term, sort } = searchState

    const searchParams: Params = {
      days: days,
      indexes,
      page: page.toString(),
      size: size.toString(),
      sort: sort.toString(),
    }
    for (const key in filters) {
      if (filters[key].length) {
        const items: string[] = filters[key].map(
          (name) => `${encodeURIComponent(name)}`,
        )
        searchParams[key] = items
      }
    }
    if (term && !term.includes('*')) {
      searchParams['term'] = term
    }
    if (searchState.community) {
      searchParams['community'] = 'true'
    }
    return searchParams
  }

  public getTagResults(tagParams: Params): Observable<TagDictionary> {
    return this.http.get<TagDictionary>(
      `${this.verityEnvironmentService.apiUrl}/es/tags/`,
      {
        params: tagParams,
      },
    )
  }

  /**
   * Search and any post-processing / follow-on requests to
   * yield fully resolved SearchResultData
   */
  public getSearchResults(
    searchParams: Params,
  ): Observable<ResolvedSearchResultData> {
    // Get response results
    return (
      this.http
        .get<SearchResultData>(
          `${this.verityEnvironmentService.apiUrl}/es/query/`,
          {
            params: searchParams,
          },
        )
        // Post-process to replace results with fully resolved payload data
        .pipe(
          switchMap((results) => {
            const resultsObservables: Array<
              Observable<ResolvedSearchResultItem>
            > = results.results.map((result) => {
              if (this.isCollectionResult(result)) {
                return this.collectionService
                  .resolveCollectionLinks(result)
                  .pipe(
                    map((resolvedResult) => {
                      return { ...resolvedResult, index: result.index }
                    }),
                  )
              } else {
                return of(result)
              }
            })

            // Pass old results with observable for follow-on requests to merge back
            return forkJoin({
              results: of(results),
              resolvedResults:
                results.results.length > 0
                  ? combineLatest(resultsObservables)
                  : of(null),
            })
          }),
          map((value) => {
            return {
              ...value.results,
              results: value.resolvedResults,
            }
          }),
        )
    )
  }

  /**
   * Reusable type guard predicates for type narrowing search results data
   * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
   */

  private isCollectionResult(
    searchResult: SearchResultItem,
  ): searchResult is CollectionRecord & SearchResultAdditionalMetadata {
    return searchResult.index == Enums.CyborgIndexEnum.CYBORG_COLLECTIONS
  }

  public isCollection(
    searchResult: ResolvedSearchResultItem,
  ): searchResult is CollectionData & SearchResultAdditionalMetadata {
    return searchResult.index == Enums.CyborgIndexEnum.CYBORG_COLLECTIONS
  }

  public isHuntPack(
    searchResult: ResolvedSearchResultItem,
  ): searchResult is HuntPackageRecord & SearchResultAdditionalMetadata {
    return searchResult.index == Enums.CyborgIndexEnum.CYBORG_USE_CASES
  }

  public isThreatProfile(
    searchResult: ResolvedSearchResultItem,
  ): searchResult is ThreatProfile & SearchResultAdditionalMetadata {
    return searchResult.index == Enums.CyborgIndexEnum.CYBORG_THREAT_PROFILES
  }
}
